/* @refresh reset */
import React, { Dispatch, SetStateAction, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import axios from '../../../sharedModules/utils/axios.utils';
import { User } from '../model';

interface AuthenticationContextInterface {
  user: User;
  setUser?: Dispatch<SetStateAction<User>>;
  logout?: () => Promise<void>;
  currentUserHasRole?: (role: string) => boolean;
  loginFormError?: string;
  setLoginError?: Dispatch<SetStateAction<string>>;
  isAdminUser?: boolean;
  isApprover?: boolean;
  fetchUser?: () => Promise<void>;
}

interface AuthenticationContextProviderProps {
  children: React.ReactNode;
}

const AuthenticationContext = React.createContext<AuthenticationContextInterface>({ user: {} });

const AuthenticationContextConsumer = AuthenticationContext.Consumer;
const AuthenticationContextProvider: React.FC<AuthenticationContextProviderProps> = ({ children }) => {
  const history = useHistory();
  const { accounts } = useMsal();

  const [user, setUser] = useState<any>();
  const [loginFormError, setLoginError] = useState<string>(undefined);

  const isAdminUser = user?.modules?.some((module) => module.value === 'admin' && parseInt(module.tier) > 2);
  const isApprover = user?.modules?.some((module) => module.value === 'chiller_logs' && module.can_review);

  const logout = async () => {
    try {
      localStorage.removeItem('token');
      sessionStorage.removeItem('redirectAfterLogin'); // Clear any stored redirect URL
      setUser({});
      window.location.href = '/login';
    } catch {
      setUser({});
    }
  };

  const currentUserHasRole = (role: string) => {
    if (user?.roles?.find((user_role: string) => user_role === role)) {
      return true;
    }

    return false;
  };

  const isMountedRef = useRef(true);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const fetchUser = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const isDevelopment = import.meta.env.DEV;

      // In development, just call /sessions without microsoft_auth_id
      // Backend will return User.first in development mode
      const endpoint = isDevelopment
        ? '/sessions'
        : `/sessions?microsoft_auth_id=${accountsRef.current[0]?.homeAccountId}&impersonation_id=${sessionStorage.getItem('impersonatedAuthId')}`;

      const result = await axios.get<any, any>(endpoint);

      if (!isMountedRef.current) return;

      // Only update user state if the data has actually changed
      const newUser = result?.data?.user;
      setUser((prevUser) => {
        if (JSON.stringify(prevUser) !== JSON.stringify(newUser)) {
          return newUser;
        }
        return prevUser;
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      console.error('Failed to fetch user:', error);
      setUser((prevUser) => {
        if (JSON.stringify(prevUser) !== JSON.stringify({})) {
          return {};
        }
        return prevUser;
      });
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // Fetch user on mount to determine authentication state
    // Backend will handle unauthenticated requests appropriately
    if (user === undefined) {
      fetchUser();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [accounts, user, fetchUser]);

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      logout,
      currentUserHasRole,
      loginFormError,
      setLoginError,
      isAdminUser,
      isApprover,
      fetchUser,
    }),
    [user, loginFormError, isAdminUser, isApprover, fetchUser],
  );

  return <AuthenticationContext.Provider value={contextValue}>{children}</AuthenticationContext.Provider>;
};
export { AuthenticationContextProvider, AuthenticationContextConsumer, AuthenticationContext };
